/**
 * TalentTrain (Prompt 20) — shared shell used by both Admin and Client portals.
 *
 * For each collaborator we render three sections:
 *   1. Competency Gap Summary  — pulled live from TalentCompetency gap analysis
 *   2. Training Plan           — CRUD on training actions (name/type/provider/duration/
 *                                target competency/status)
 *   3. Schedule / Timeline     — start + end dates per action, status updates inline
 *
 * The portal passes in an `api` adapter so the same UI works for both
 * `/admin/talent-train/...` and `/client/talent-train/...` endpoints.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  GraduationCap, Plus, Trash2, ArrowLeft, ArrowRight, Calendar as CalIcon,
  Target, ListChecks, Sparkles, Pencil, MessageSquare, FolderOpen,
} from 'lucide-react';
import { DocumentPanel } from '@/components/shared/DocumentPanel';
import TrainingDiscussion from '@/components/shared/TrainingDiscussion';
import { useDataLabel } from '@/lib/dataI18n';
import { DonutCard, SEM } from '@/components/client/SuiteAnalytics';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/components/ui/use-confirm';

const ACTION_TYPES = [
  { v: 'course',    label: 'tt.types.course' },
  { v: 'seminar',   label: 'tt.types.seminar' },
  { v: 'workshop',  label: 'tt.types.workshop' },
  { v: 'coaching',  label: 'tt.types.coaching' },
  { v: 'elearning', label: 'tt.types.elearning' },
  { v: 'other',     label: 'tt.types.other' },
];
const ACTION_STATUSES = [
  { v: 'planned',     label: 'tt.statuses.planned',     tone: 'bg-muted text-foreground' },
  { v: 'in_progress', label: 'tt.statuses.inProgress', tone: 'bg-amber-100 text-amber-800' },
  { v: 'completed',   label: 'tt.statuses.completed',   tone: 'bg-emerald-100 text-emerald-800' },
];
const COLOR_HEX = { green: '#16a34a', yellow: '#ca8a04', red: '#dc2626', no_data: '#94a3b8' };

// UAT A1 — date-only strings ('YYYY-MM-DD') must be rendered in the LOCAL
// calendar day. `new Date('2026-10-19')` parses as UTC midnight and shows
// Oct 18 in timezones west of UTC; parsing the parts avoids the shift.
export function parseDateOnly(v) {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const blankAction = {
  name: '', type: 'course', provider: '', duration: '',
  target_competency_code: '', status: 'planned',
  start_date: '', end_date: '',
  requires_recert: false, recert_due_date: '', evidence_location: '',
};

export default function TalentTrainShell({
  portal,                 // 'admin' | 'client'
  canEdit,                // bool — write guard for the official plan
  canDiscuss = undefined, // bool — Observations & Follow-up write guard
  api,                    // { list, getUser, create, update, remove }
  initialUserId = null,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [view, setView] = useState(initialUserId ? 'detail' : 'list');
  const [activeUid, setActiveUid] = useState(initialUserId);
  const [seedAssignmentId, setSeedAssignmentId] = useState(params.get('seed') || null);
  // Alert deep link — highlight (not modify) a specific action in the plan.
  const focusActionId = params.get('focus') || null;

  useEffect(() => {
    if (initialUserId) { setActiveUid(initialUserId); setView('detail'); }
  }, [initialUserId]);

  return (
    <>
      <Topbar
        title="TalentTrain"
        subtitle={t('tt.subtitle', { defaultValue: 'Learning needs assessment and personalised development plans' })}
        actions={view === 'detail' ? (
          <Button
            variant="ghost" size="sm"
            data-testid="tt-back-to-list"
            onClick={() => {
              setView('list'); setActiveUid(null);
              navigate(portal === 'admin' ? '/admin/talenttrain' : '/client/talenttrain');
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('tt.allCollaborators', { defaultValue: 'All collaborators' })}
          </Button>
        ) : null}
      />
      <div className="p-6 lg:p-8 space-y-6">
        {view === 'list' ? (
          <CollaboratorList
            api={api}
            portal={portal}
            onOpen={(uid) => { setActiveUid(uid); setView('detail'); }}
          />
        ) : (
          <UserDetail
            uid={activeUid}
            api={api}
            portal={portal}
            canEdit={canEdit}
            canDiscuss={canDiscuss ?? canEdit}
            seedAssignmentId={seedAssignmentId}
            clearSeedHint={() => setSeedAssignmentId(null)}
            highlightActionId={focusActionId}
          />
        )}
      </div>
    </>
  );
}

// ---------- Collaborator list ---------------------------------------------

function CollaboratorList({ api, portal, onOpen }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMap, setUnreadMap] = useState({});
  const navigate = useNavigate();
  useEffect(() => {
    setLoading(true);
    api.list().then(setData).catch(() => toast.error(t('toasts.failedToLoad'))).finally(() => setLoading(false));
    api.discussionUnread?.().then(setUnreadMap).catch(() => {});
  }, [api]);

  if (loading) return <p className="text-sm text-muted-foreground" data-testid="tt-loading">{t('common.loadingDots')}</p>;
  if (!data) return <p className="text-sm italic text-muted-foreground">{t('common.noData')}</p>;

  // Founder UAT 2026-06 — training status distribution from real action counts.
  const ttAgg = (data.users || []).reduce((acc, u) => {
    const ac = u.action_counts || {};
    acc.planned += ac.planned || 0;
    acc.in_progress += ac.in_progress || 0;
    acc.completed += ac.completed || 0;
    return acc;
  }, { planned: 0, in_progress: 0, completed: 0 });
  const ttTotal = ttAgg.planned + ttAgg.in_progress + ttAgg.completed;

  return (
    <>
    {ttTotal > 0 && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4" data-testid="tt-analytics-row">
        <DonutCard
          title={t('tt.analytics.statusTitle', { defaultValue: 'Acciones de capacitación por estado' })}
          testid="tt-chart-status"
          accent={SEM.orange}
          data={[
            { name: t('tt.analytics.planned', { defaultValue: 'Planificadas' }), value: ttAgg.planned, color: SEM.yellow },
            { name: t('tt.analytics.inProgress', { defaultValue: 'En progreso' }), value: ttAgg.in_progress, color: SEM.orange },
            { name: t('tt.analytics.completed', { defaultValue: 'Completadas' }), value: ttAgg.completed, color: SEM.green },
          ]}
          emptyText={t('tt.analytics.empty', { defaultValue: 'Sin acciones de capacitación todavía.' })}
        />
        <div className="rounded-lg border bg-card shadow-card p-5 relative overflow-hidden" data-testid="tt-completion-rate">
          <span className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: SEM.green }} aria-hidden="true" />
          <h3 className="font-semibold text-base mb-1">{t('tt.analytics.rateTitle', { defaultValue: 'Tasa de finalización' })}</h3>
          <p className="text-4xl font-bold tabular-nums mt-3" style={{ color: SEM.green }}>
            {Math.round((ttAgg.completed / ttTotal) * 100)}%
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('tt.analytics.rateSubtitle', { defaultValue: '{{done}} de {{total}} acciones completadas', done: ttAgg.completed, total: ttTotal })}
          </p>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden mt-3">
            <div className="h-full rounded-full" style={{ width: `${Math.round((ttAgg.completed / ttTotal) * 100)}%`, backgroundColor: SEM.green }} />
          </div>
        </div>
      </div>
    )}
    <Card className="p-0 shadow-card overflow-hidden" data-testid="tt-list-card">
      <div className="p-5 border-b flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <GraduationCap className="h-5 w-5" /> {t('tt.collaborators')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('tt.pickPerson')}
          </p>
        </div>
        <Badge variant="outline" className="text-xs">{t('tt.totalBadge', { n: data.users.length })}</Badge>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[15px]">
          <thead className="bg-muted/40">
            <tr className="text-left text-[13px] uppercase tracking-wider text-muted-foreground">
              <th className="py-2.5 px-4">{t('tt.collaborator')}</th>
              <th className="py-2.5 px-4">{t('common.position')}</th>
              <th className="py-2.5 px-4">{t('tt.gapSource', { defaultValue: 'Gap source (TalentMap)' })}</th>
              <th className="py-2.5 px-4">{t('tt.gapDistribution')}</th>
              <th className="py-2.5 px-4">{t('common.actions')}</th>
              <th className="py-2.5 px-4 text-right">{t('common.open')}</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((r) => {
              const s = r.summary; const total = s.green + s.yellow + s.red + s.no_data || 1;
              const ac = r.action_counts;
              return (
                <tr key={r.user.id} className="border-t hover:bg-muted/30" data-testid={`tt-user-row-${r.user.id}`}>
                  <td className="py-3 px-4 font-medium">
                    {r.user.name}
                    {(unreadMap[r.user.id] || 0) > 0 && (
                      <Badge className="ml-2 bg-primary text-primary-foreground text-xs px-1.5" data-testid={`tt-unread-badge-${r.user.id}`}>
                        <MessageSquare className="h-2.5 w-2.5 mr-0.5" />{unreadMap[r.user.id]}
                      </Badge>
                    )}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    {r.user.position || '—'}
                    {r.plan_seeded && <Badge variant="outline" className="ml-2 text-xs">{t('tt.seeded')}</Badge>}
                    {(r.evo_handoff_count || 0) > 0 && (
                      <Badge variant="outline" className="ml-2 text-xs text-teal-700 border-teal-300"
                             data-testid={`tt-evo-count-${r.user.id}`}>
                        EvO · {r.evo_handoff_count}
                      </Badge>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className={r.has_talentmap ? 'text-emerald-700 border-emerald-300' : 'text-muted-foreground'}>
                      {r.has_talentmap ? t('tt.released') : t('tt.pending')}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex h-2 rounded-full overflow-hidden w-44 bg-muted/40">
                      {['green', 'yellow', 'red', 'no_data'].map((k) => (
                        s[k] > 0
                          ? <div key={k} style={{ width: `${(s[k] / total) * 100}%`, background: COLOR_HEX[k] }} title={`${k}: ${s[k]}`} />
                          : null
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm tabular-nums text-muted-foreground">
                    {t('tt.totalDots', { total: ac.total, ip: ac.in_progress, done: ac.completed })}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => {
                        onOpen(r.user.id);
                        navigate(portal === 'admin'
                          ? `/admin/talenttrain/${r.user.id}`
                          : `/client/talenttrain/${r.user.id}`);
                      }}
                      data-testid={`tt-user-open-${r.user.id}`}
                    >
                      {t('tt.openBtn')} <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!data.users.length && (
              <tr><td colSpan={6} className="py-6 text-center italic text-muted-foreground">{t('tt.noCollaborators')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
    </>
  );
}

// ---------- User detail (Sections 1, 2, 3) --------------------------------

function UserDetail({ uid, api, portal, canEdit, canDiscuss, seedAssignmentId, clearSeedHint, highlightActionId }) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const navigate = useNavigate();
  const dataLabel = useDataLabel();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('plan');
  const [discUnread, setDiscUnread] = useState(0);
  useEffect(() => {
    api.discussionUnread?.().then((m) => setDiscUnread(m?.[uid] || 0)).catch(() => {});
  }, [api, uid]);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(blankAction);
  const [editingId, setEditingId] = useState(null);
  const [docsAction, setDocsAction] = useState(null); // V5.3 §B — certificates
  const [askConfirm, ConfirmUi] = useConfirm();

  const reload = () => {
    setLoading(true);
    api.getUser(uid).then(setData).catch(() => toast.error(t('toasts.failedToLoadPlan'))).finally(() => setLoading(false));
  };
  useEffect(reload, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toast once when a seed query param routed us in (Prompt 21).
  useEffect(() => {
    if (seedAssignmentId && data?.plan?.source_assignment_id === seedAssignmentId) {
      toast.success(t('toasts.planPrePopulated'));
      clearSeedHint?.();
    }
  }, [seedAssignmentId, data, clearSeedHint]);

  // Alert deep link — scroll to and highlight the expiring action (read-only).
  useEffect(() => {
    if (!highlightActionId || loading || !data) return;
    const el = document.querySelector(`[data-testid="tt-action-${highlightActionId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightActionId, loading, data]);

  const competencyOptions = useMemo(() => {
    const set = new Map();
    (data?.gap?.competencies || []).forEach((c) =>
      set.set(c.competency_code, c.competency_label));
    (data?.plan?.source_gaps || []).forEach((c) =>
      set.set(c.competency_code, c.competency_label));
    (data?.evo_handoffs || []).forEach((h) =>
      set.set(h.competency_code, isES
        ? (h.competency_name_es || h.competency_name || h.competency_code)
        : (h.competency_name || h.competency_name_es || h.competency_code)));
    return [...set.entries()].map(([code, label]) => ({ code, label }));
  }, [data, isES]);

  // UAT A3 — localized competency names everywhere (never raw codes like safety_compliance)
  const compLabelOf = (code) => {
    if (!code) return '—';
    const hit = competencyOptions.find((c) => c.code === code);
    return dataLabel(hit?.label || code);
  };

  if (loading || !data) return <p className="text-sm text-muted-foreground" data-testid="tt-detail-loading">{t('common.loadingDots')}</p>;

  const u = data.user;
  const gap = data.gap;
  const actions = data.actions || [];

  const openCreate = () => {
    setEditingId(null);
    setEditDraft({
      ...blankAction,
      target_competency_code: competencyOptions[0]?.code || '',
    });
    setEditOpen(true);
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    setEditDraft({
      name: a.name || '', type: a.type || 'course', provider: a.provider || '',
      duration: a.duration || '', target_competency_code: a.target_competency_code || '',
      status: a.status || 'planned',
      start_date: a.start_date || '', end_date: a.end_date || '',
      requires_recert: !!a.requires_recert, recert_due_date: a.recert_due_date || '',
      evidence_location: a.evidence_location || '',
    });
    setEditOpen(true);
  };
  const save = async () => {
    if (!editDraft.name.trim()) return toast.error(t('toasts.nameRequired'));
    try {
      if (editingId) {
        await api.update(editingId, editDraft);
        toast.success(t('toasts.actionUpdated'));
      } else {
        await api.create(uid, editDraft);
        toast.success(t('toasts.actionAdded'));
      }
      setEditOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const remove = async (aid) => {
    // BUG 06b — use canonical delete dialog copy
    const ok = await askConfirm({ kind: 'delete', testId: 'tt-action-confirm' });
    if (!ok) return;
    try { await api.remove(aid); toast.success(t('toasts.removed')); reload(); }
    catch (e) { toast.error(t('toasts.failedToRemove')); }
  };
  const quickStatus = async (aid, status) => {
    try { await api.update(aid, { status }); reload(); }
    catch (e) { toast.error(t('toasts.failedUpdateStatus')); }
  };

  return (
    <>
      {/* Identity */}
      <Card className="p-5 shadow-card flex items-start justify-between flex-wrap gap-3" data-testid="tt-identity">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{u.name}</h2>
          <p className="text-sm text-muted-foreground">
            {u.position || '—'} · {u.email || ''}
          </p>
          {data.plan?.source_assignment_id && (
            <Badge variant="outline" className="mt-2 text-xs">
              <Sparkles className="h-3 w-3 mr-1" /> {t('tt.seededBadge')}
            </Badge>
          )}
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted">
          <TabsTrigger value="gap" data-testid="tt-tab-gap"><Target className="h-4 w-4 mr-1.5" /> {t('tt.tabs.gapSummary')}</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tt-tab-plan"><ListChecks className="h-4 w-4 mr-1.5" /> {t('tt.tabs.trainingPlan')}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tt-tab-timeline"><CalIcon className="h-4 w-4 mr-1.5" /> {t('tt.tabs.schedule')}</TabsTrigger>
          <TabsTrigger value="discussion" data-testid="tt-tab-discussion">
            <MessageSquare className="h-4 w-4 mr-1.5" /> {t('tt.tabs.discussion')}
            {discUnread > 0 && (
              <Badge className="ml-1.5 bg-primary text-primary-foreground text-xs px-1.5" data-testid="tt-discussion-unread-badge">
                {discUnread}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* SECTION 1 — gap summary */}
        <TabsContent value="gap" className="mt-4">
          <GapSummary gap={gap} sourceGaps={data.plan?.source_gaps} evoHandoffs={data.evo_handoffs} />
        </TabsContent>

        {/* SECTION 4 — consulting discussion (HM <-> Client Admin, employee never sees it) */}
        <TabsContent value="discussion" className="mt-4">
          <TrainingDiscussion
            dapi={api}
            uid={uid}
            isHm={portal === 'admin'}
            canWrite={canDiscuss ?? canEdit}
            onUnreadCleared={() => setDiscUnread(0)}
          />
        </TabsContent>

        {/* SECTION 2 — training plan */}
        <TabsContent value="plan" className="mt-4">
          <Card className="p-5 shadow-card" data-testid="tt-plan-card">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="font-display font-bold text-lg">{t('tt.trainingPlan')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('tt.trainingPlanDesc')}
                </p>
              </div>
              {canEdit && (
                <Button onClick={openCreate} data-testid="tt-plan-add" className="bg-gradient-primary">
                  <Plus className="h-4 w-4 mr-1.5" /> {t('tt.addTrainingAction')}
                </Button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-[15px]">
                <thead>
                  <tr className="text-left text-[13px] uppercase tracking-wider text-muted-foreground border-b">
                    <th className="py-2 pr-3">{t('common.name')}</th>
                    <th className="py-2 px-3">{t('common.type')}</th>
                    <th className="py-2 px-3">{t('tt.provider')}</th>
                    <th className="py-2 px-3">{t('common.duration')}</th>
                    <th className="py-2 px-3">{t('tt.targetCompetency')}</th>
                    <th className="py-2 px-3">{t('common.status')}</th>
                    {canEdit && <th className="py-2 pl-3 w-20"></th>}
                  </tr>
                </thead>
                <tbody>
                  {actions.map((a) => {
                    const tone = ACTION_STATUSES.find((s) => s.v === a.status)?.tone || 'bg-muted';
                    const compLabel = dataLabel(competencyOptions.find((c) => c.code === a.target_competency_code)?.label
                      || a.target_competency_code || null, '—');
                    return (
                      <tr key={a.id}
                          className={`border-b last:border-0 ${a.id === highlightActionId
                            ? 'bg-warning/10 ring-2 ring-inset ring-warning/60'
                            : 'hover:bg-muted/30'}`}
                          data-testid={`tt-action-${a.id}`}>
                        <td className="py-3 pr-3 font-medium">
                          {a.name}
                          <button type="button"
                                  title={t('tt.actionDocuments', { defaultValue: isES ? 'Constancias y documentos' : 'Certificates & documents' })}
                                  onClick={() => setDocsAction(a)}
                                  className="ml-1.5 inline-flex align-middle text-teal-600 hover:text-teal-800"
                                  data-testid={`tt-action-docs-${a.id}`}>
                            <FolderOpen className="h-4 w-4" />
                          </button>
                          <RecertChip action={a} />
                          {portal === 'client' && a.status === 'completed' && (
              <button
                type="button"
                onClick={() => navigate(`/client/talentcompetency?evaluate=${uid}&fromTraining=${encodeURIComponent(a.name)}&trainingId=${a.id}`)}
                              className="block mt-1 text-sm text-primary underline underline-offset-2"
                              data-testid={`tt-evaluate-knowledge-${a.id}`}
                            >
                              {t('tt.evaluateKnowledge', { defaultValue: 'Evaluar conocimientos' })}
                            </button>
                          )}
                          {a.evidence_location ? (
                            /^https?:\/\//.test(a.evidence_location) ? (
                              <a href={a.evidence_location} target="_blank" rel="noopener noreferrer"
                                 className="block text-sm text-primary underline underline-offset-2 truncate max-w-[260px]"
                                 data-testid={`tt-evidence-link-${a.id}`}>
                                {t('tt.evidenceLocation')}
                              </a>
                            ) : (
                              <span className="block text-sm text-muted-foreground truncate max-w-[260px]"
                                    data-testid={`tt-evidence-ref-${a.id}`}>
                                {t('tt.evidenceLocation')}: {a.evidence_location}
                              </span>
                            )
                          ) : null}
                        </td>
                        <td className="py-3 px-3 capitalize">{(() => { const lab = ACTION_TYPES.find((tt) => tt.v === a.type)?.label; return lab ? t(lab) : a.type; })()}</td>
                        <td className="py-3 px-3 text-muted-foreground">{a.provider || '—'}</td>
                        <td className="py-3 px-3 text-muted-foreground tabular-nums">{a.duration || '—'}</td>
                        <td className="py-3 px-3 capitalize">{compLabel}</td>
                        <td className="py-3 px-3">
                          {canEdit ? (
                            <Select value={a.status} onValueChange={(v) => quickStatus(a.id, v)}>
                              <SelectTrigger className={`h-8 w-36 text-xs font-bold uppercase tracking-wider ${tone}`} data-testid={`tt-action-status-${a.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ACTION_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{t(s.label)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold uppercase ${tone}`}>
                              {(() => { const lab = ACTION_STATUSES.find((s) => s.v === a.status)?.label; return lab ? t(lab) : ''; })()}
                            </span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="py-3 pl-3 text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`tt-action-edit-${a.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`tt-action-del-${a.id}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!actions.length && (
                    <tr><td colSpan={canEdit ? 7 : 6} className="py-6 text-center italic text-muted-foreground">
                      {t('tt.noActionsYet')} {canEdit ? t('tt.addOneToStart') : ''}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* SECTION 3 — schedule */}
        <TabsContent value="timeline" className="mt-4">
          <ScheduleSection
            actions={actions}
            canEdit={canEdit}
            compLabelOf={compLabelOf}
            onUpdate={async (aid, patch) => { await api.update(aid, patch); reload(); }}
          />
        </TabsContent>
      </Tabs>

      {/* V5.3 §B — training certificates & supporting documents */}
      <Dialog open={!!docsAction} onOpenChange={(o) => !o && setDocsAction(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="tt-action-docs-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {isES ? 'Constancias y documentos — ' : 'Certificates & documents — '}{docsAction?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isES ? 'Documentos de la acción formativa' : 'Training action documents'}
            </DialogDescription>
          </DialogHeader>
          {docsAction && (
            <DocumentPanel suite="talenttrain" entityType="training_action"
                           entityId={docsAction.id}
                           employeeUserId={u.id}
                           defaultDocType="training_certificate"
                           docTypes={["training_certificate", "diploma", "certification", "evidence", "other"]}
                           mode={portal === 'client' ? 'client' : 'admin'}
                           companyId={portal === 'client' ? null : u.company_id}
                           readOnly={portal !== 'client'}
                           compact />
          )}
        </DialogContent>
      </Dialog>

      {/* Action editor dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl" data-testid="tt-action-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? t('tt.editTrainingAction') : t('tt.addTrainingAction')}</DialogTitle>
            <DialogDescription>
              {t('tt.dialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>{t('common.nameRequired')}</Label>
              <Input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} data-testid="tt-action-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('common.type')}</Label>
                <Select value={editDraft.type} onValueChange={(v) => setEditDraft({ ...editDraft, type: v })}>
                  <SelectTrigger data-testid="tt-action-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTION_TYPES.map((opt) => <SelectItem key={opt.v} value={opt.v}>{t(opt.label)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('common.status')}</Label>
                <Select value={editDraft.status} onValueChange={(v) => setEditDraft({ ...editDraft, status: v })}>
                  <SelectTrigger data-testid="tt-action-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTION_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{t(s.label)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>{t('tt.providerInstitution')}</Label>
                <Input value={editDraft.provider} onChange={(e) => setEditDraft({ ...editDraft, provider: e.target.value })} data-testid="tt-action-provider" />
              </div>
              <div><Label>{t('common.duration')}</Label>
                <Input placeholder={t('tt.durationPlaceholder')} value={editDraft.duration} onChange={(e) => setEditDraft({ ...editDraft, duration: e.target.value })} data-testid="tt-action-duration" />
              </div>
            </div>
            <div>
              <Label>{t('tt.targetCompetency')}</Label>
              <Select value={editDraft.target_competency_code} onValueChange={(v) => setEditDraft({ ...editDraft, target_competency_code: v })}>
                <SelectTrigger data-testid="tt-action-comp"><SelectValue placeholder={t('tt.pickGap')} /></SelectTrigger>
                <SelectContent>
                  {competencyOptions.length === 0 && <SelectItem value="__none" disabled>{t('tt.noGapsAddInTc')}</SelectItem>}
                  {competencyOptions.map((c) => (
                    <SelectItem key={c.code} value={c.code} className="capitalize">{dataLabel(c.label || c.code)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('tt.startDate')}</Label>
                <Input type="date" value={editDraft.start_date || ''} onChange={(e) => setEditDraft({ ...editDraft, start_date: e.target.value })} data-testid="tt-action-start" />
              </div>
              <div><Label>{t('common.endDate')}</Label>
                <Input type="date" value={editDraft.end_date || ''} onChange={(e) => setEditDraft({ ...editDraft, end_date: e.target.value })} data-testid="tt-action-end" />
              </div>
            </div>
            <div className="rounded border bg-muted/20 p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editDraft.requires_recert}
                  onChange={(e) => setEditDraft({ ...editDraft, requires_recert: e.target.checked })}
                  data-testid="tt-action-recert-check"
                />
                {t('tt.requiresRecert')}
              </label>
              {editDraft.requires_recert && (
                <div>
                  <Label className="text-sm">{t('tt.recertDueDate')}</Label>
                  <Input type="date" value={editDraft.recert_due_date || ''}
                         onChange={(e) => setEditDraft({ ...editDraft, recert_due_date: e.target.value })}
                         data-testid="tt-action-recert-date" />
                </div>
              )}
            </div>
            {editDraft.status === 'completed' && (
              <div>
                <Label>{t('tt.evidenceLocation')}</Label>
                <Input value={editDraft.evidence_location || ''}
                       placeholder={t('tt.evidencePlaceholder')}
                       onChange={(e) => setEditDraft({ ...editDraft, evidence_location: e.target.value })}
                       data-testid="tt-action-evidence" />
                <p className="text-sm text-muted-foreground mt-1">{t('tt.evidenceHint')}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={save} data-testid="tt-action-save" className="bg-gradient-primary">
              {editingId ? t('tt.saveChanges') : t('tt.addAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ConfirmUi}
    </>
  );
}

// ---------- Section 1: gap summary ----------------------------------------

// Founder 2026-08 — recert status chip (Current / Expiring soon / Expired).
export function recertState(a) {
  if (!a?.requires_recert || !a?.recert_due_date) return null;
  const due = new Date(`${a.recert_due_date}T23:59:59Z`);
  if (Number.isNaN(due.getTime())) return null;
  const now = new Date();
  if (due < now) return 'expired';
  if ((due - now) / 86400000 <= 30) return 'expiring';
  return 'current';
}

function RecertChip({ action }) {
  const { t, i18n } = useTranslation();
  const state = recertState(action);
  if (!state) return null;
  const cls = state === 'expired'
    ? 'bg-destructive/15 text-destructive border-destructive/30'
    : state === 'expiring'
      ? 'bg-warning/15 text-warning border-warning/30'
      : 'bg-success/15 text-success border-success/30';
  let dateLabel = action.recert_due_date;
  try {
    dateLabel = new Date(`${action.recert_due_date}T12:00:00Z`).toLocaleDateString(
      i18n.language === 'es' ? 'es-PA' : 'en-US',
      { day: '2-digit', month: 'short', year: 'numeric' },
    );
  } catch { /* keep ISO fallback */ }
  return (
    <span className="inline-flex items-center gap-1.5 ml-2 whitespace-nowrap">
      <Badge className={`text-sm ${cls}`} data-testid={`tt-recert-chip-${action.id}`}>
        {t(`tt.recertState.${state}`)}
      </Badge>
      <span className="text-sm text-muted-foreground" data-testid={`tt-recert-due-${action.id}`}>
        {t('tt.recertDue', { date: dateLabel })}
      </span>
    </span>
  );
}

function GapSummary({ gap, sourceGaps, evoHandoffs }) {
  const { t, i18n } = useTranslation();
  const dataLabel = useDataLabel();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const evo = evoHandoffs || [];
  const fmtD = (v) => {
    if (!v) return '—';
    const d = parseDateOnly(v);
    if (!d) return v;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };
  const EVO_STATUS = {
    red:    { es: 'Brecha prioritaria', en: 'Priority gap',  cls: 'bg-rose-100 text-rose-800' },
    yellow: { es: 'Brecha moderada',    en: 'Moderate gap',  cls: 'bg-amber-100 text-amber-800' },
  };

  const evoSection = evo.length > 0 && (
    <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-4 space-y-2" data-testid="tt-evo-handoffs">
      <div className="flex items-center gap-2 flex-wrap">
        <Target className="h-4 w-4 text-teal-700" />
        <p className="font-semibold text-sm text-teal-900">
          {L('Brechas transferidas desde Brechas de Competencias (TalentCompetency)',
             'Gaps transferred from Competency Gaps (TalentCompetency)')}
        </p>
        <Badge variant="outline" className="text-xs bg-white" data-testid="tt-evo-handoffs-count">{evo.length}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {L('Registros históricos inmutables: cada traslado conserva el nivel esperado, observado y la brecha al momento de su medición de origen. Nuevas mediciones nunca sobrescriben estos registros.',
           'Immutable historical records: each transfer preserves the expected, observed and gap values at its source measurement. New measurements never overwrite these records.')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[12px] uppercase tracking-wider text-muted-foreground border-b border-teal-200/60">
              <th className="py-2 pr-3">{t('tt.competency')}</th>
              <th className="py-2 px-3">{L('Medición', 'Measurement')}</th>
              <th className="py-2 px-3 text-right tabular-nums">{L('Esperado', 'Expected')}</th>
              <th className="py-2 px-3 text-right tabular-nums">{L('Observado', 'Observed')}</th>
              <th className="py-2 px-3 text-right tabular-nums">{L('Brecha', 'Gap')}</th>
              <th className="py-2 px-3">{L('Transferido', 'Transferred')}</th>
              <th className="py-2 px-3 text-right">{L('Estado', 'Status')}</th>
            </tr>
          </thead>
          <tbody>
            {evo.map((h) => {
              const sm = EVO_STATUS[h.status] || EVO_STATUS.yellow;
              return (
                <tr key={h.id} className="border-b border-teal-100/70 last:border-0" data-testid={`tt-evo-handoff-${h.competency_code}`}>
                  <td className="py-2 pr-3 font-medium">
                    {isES ? (h.competency_name_es || h.competency_name) : (h.competency_name || h.competency_name_es)}
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{fmtD(h.measurement_date)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{h.expected_at_handoff ?? h.expected ?? '—'}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{h.observed_at_handoff ?? h.observed ?? '—'}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold">{h.gap_at_handoff ?? h.gap ?? '—'}</td>
                  <td className="py-2 px-3 text-muted-foreground">{fmtD(h.created_at)}</td>
                  <td className="py-2 px-3 text-right">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${sm.cls}`}>
                      {isES ? sm.es : sm.en}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (!gap) {
    return (
      <Card className="p-5 shadow-card space-y-4">
        {evoSection || (
          <p className="text-sm text-muted-foreground italic">{t('tt.noGapDataMsg')}</p>
        )}
      </Card>
    );
  }
  const comps = gap.competencies || [];
  const s = gap.summary || { green: 0, yellow: 0, red: 0, no_data: 0 };
  // UX rule (Founder UAT): when there is NO legacy competency-requirement data
  // but valid transferred gaps exist, hide the contradictory zero-summary —
  // the transferred Competency Gaps are the actionable source.
  const showLegacy = comps.length > 0 || evo.length === 0;

  const seededCodes = new Set((sourceGaps || []).map((c) => c.competency_code));

  return (
    <Card className="p-5 shadow-card space-y-4" data-testid="tt-gap-summary">
      <div>
        <h3 className="font-display font-bold text-lg">{t('tt.gapSectionTitle')}</h3>
        {showLegacy && (
          <p className="text-sm text-muted-foreground" data-testid="tt-gap-source-note">
            {t('tt.gapSourceNote')}
          </p>
        )}
      </div>

      {evoSection}

      {showLegacy && (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
        <Stat label={t('common.competencies')} value={s.green + s.yellow + s.red + s.no_data} tone="text-foreground" />
        <Stat label={t('common.meets')} value={s.green} tone="text-emerald-600" />
        <Stat label={t('common.close')} value={s.yellow} tone="text-amber-600" />
        <Stat label={t('common.significant')} value={s.red} tone="text-rose-600" />
        <Stat label={t('common.noData')} value={s.no_data} tone="text-muted-foreground" />
      </div>

      {(sourceGaps || []).length > 0 && (
        <div className="text-sm text-muted-foreground italic flex items-center gap-1.5" data-testid="tt-gap-seed-note">
          <Sparkles className="h-3.5 w-3.5" />
          {t('tt.seedSnapshot', { n: sourceGaps.length })}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-[15px]">
          <thead>
            <tr className="text-left text-[13px] uppercase tracking-wider text-muted-foreground border-b">
              <th className="py-2 pr-3">{t('tt.competency')}</th>
              <th className="py-2 px-3 text-right tabular-nums">{t('common.required')}</th>
              <th className="py-2 px-3 text-right tabular-nums">{t('common.actual')}</th>
              <th className="py-2 px-3 text-right tabular-nums">{t('common.gap')}</th>
              <th className="py-2 px-3 text-right">{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {comps.filter((c) => c.color !== 'green').map((c, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-2 pr-3 capitalize">
                  {dataLabel(c.competency_label)}
                  {seededCodes.has(c.competency_code) && (
                    <Badge variant="outline" className="ml-2 text-sm">{t('tt.inSeed')}</Badge>
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">{c.required_level?.toFixed(1)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.actual_level == null ? '—' : c.actual_level.toFixed(2)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.gap == null ? '—' : (c.gap > 0 ? '+' : '') + c.gap.toFixed(2)}</td>
                <td className="py-2 px-3 text-right">
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold uppercase"
                        style={{ background: `${COLOR_HEX[c.color]}18`, color: COLOR_HEX[c.color] }}>
                    {c.color === 'no_data' ? '—' : c.color}
                  </span>
                </td>
              </tr>
            ))}
            {comps.filter((c) => c.color !== 'green').length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center italic text-muted-foreground" data-testid="tt-gap-empty-state">
                {comps.length === 0 ? t('tt.noTcRequirements') : t('tt.noGapsMet')}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}
    </Card>
  );
}

// ---------- Section 3: schedule / timeline -------------------------------

function ScheduleSection({ actions, canEdit, onUpdate, compLabelOf }) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'es').startsWith('es') ? 'es' : 'en';
  const ranged = actions.filter((a) => a.start_date || a.end_date);
  const timeline = useMemo(() => buildTimeline(ranged, locale), [ranged, locale]);

  return (
    <Card className="p-5 shadow-card space-y-4" data-testid="tt-timeline-card">
      <div>
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          <CalIcon className="h-5 w-5" /> {t('tt.schedule')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('tt.scheduleHint')}
        </p>
      </div>

      {/* Date editor list (works in every browser; primary editor) */}
      <div className="space-y-2">
        {actions.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 rounded border bg-muted/20 flex-wrap" data-testid={`tt-sched-row-${a.id}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="font-medium text-[15px]">{a.name}</p>
              <p className="text-sm text-muted-foreground">{compLabelOf ? compLabelOf(a.target_competency_code) : (a.target_competency_code || '—')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-[13px] uppercase text-muted-foreground">{t('common.start')}</Label>
              <Input
                type="date" value={a.start_date || ''} disabled={!canEdit}
                onChange={(e) => onUpdate(a.id, { start_date: e.target.value })}
                className="h-8 w-36" data-testid={`tt-sched-start-${a.id}`}
              />
              <Label className="text-[13px] uppercase text-muted-foreground">{t('tt.end')}</Label>
              <Input
                type="date" value={a.end_date || ''} disabled={!canEdit}
                onChange={(e) => onUpdate(a.id, { end_date: e.target.value })}
                className="h-8 w-36" data-testid={`tt-sched-end-${a.id}`}
              />
              {canEdit && (
                <Select value={a.status} onValueChange={(v) => onUpdate(a.id, { status: v })}>
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid={`tt-sched-status-${a.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTION_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{t(s.label)}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
        ))}
        {!actions.length && (
          <p className="py-6 text-center italic text-muted-foreground text-[15px]">
            {t('tt.noActionsToSchedule')}
          </p>
        )}
      </div>

      {/* Mini Gantt */}
      {timeline && (
        <div className="border-t pt-4">
          <p className="text-[13px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
            {t('tt.visualTimeline', { start: timeline.startLabel, end: timeline.endLabel })}
          </p>
          <div className="space-y-1.5" data-testid="tt-gantt">
            {timeline.bars.map((b) => {
              const tone = ACTION_STATUSES.find((s) => s.v === b.status)?.tone || 'bg-muted';
              return (
                <div key={b.id} className="flex items-center gap-2">
                  {/* UAT A2 — full title stays readable: wraps on wide screens instead of truncating */}
                  <div className="w-44 md:w-64 shrink-0 text-sm leading-snug break-words" title={b.name} data-testid={`tt-gantt-label-${b.id}`}>{b.name}</div>
                  <div className="flex-1 relative h-6 bg-muted/30 rounded overflow-hidden">
                    <div
                      className={`absolute h-full rounded ${tone} flex items-center justify-center text-xs font-bold uppercase tracking-wider`}
                      style={{ left: `${b.leftPct}%`, width: `${Math.max(b.widthPct, 4)}%` }}
                      title={`${b.name} · ${b.start} → ${b.end}`}
                    >
                      {b.widthPct >= 14 && (() => { const lab = ACTION_STATUSES.find((s) => s.v === b.status)?.label; return lab ? t(lab) : ''; })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function buildTimeline(actions, locale) {
  const valid = actions.filter((a) => a.start_date && a.end_date);
  if (!valid.length) return null;
  const min = valid.reduce((m, a) => (a.start_date < m ? a.start_date : m), valid[0].start_date);
  const max = valid.reduce((m, a) => (a.end_date > m ? a.end_date : m), valid[0].end_date);
  const span = Math.max(1, (Date.parse(max) - Date.parse(min)) / 86400000);
  const fmt = (d) => (parseDateOnly(d) || new Date(d)).toLocaleDateString(
    locale, { day: '2-digit', month: 'short', year: 'numeric' });
  return {
    startLabel: fmt(min), endLabel: fmt(max),
    bars: valid.map((a) => {
      const leftDays = (Date.parse(a.start_date) - Date.parse(min)) / 86400000;
      const widthDays = Math.max(1, (Date.parse(a.end_date) - Date.parse(a.start_date)) / 86400000);
      return {
        id: a.id, name: a.name, status: a.status,
        start: a.start_date, end: a.end_date,
        leftPct: (leftDays / span) * 100,
        widthPct: (widthDays / span) * 100,
      };
    }),
  };
}

function Stat({ label, value, tone }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className={`font-display text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}
